import React, { useEffect, useState } from "react";
import Select, { components } from "react-select";
import { showError } from "toast/toast";

export interface BillingShippingApprovalModalProps {
  open: boolean;
  loading?: boolean;
  sameAsBilling: boolean;
  setSameAsBilling: (next: boolean) => void;
  selectedBillingAddressId: string;
  setSelectedBillingAddressId: (id: string) => void;
  selectedShippingAddressId: string;
  setSelectedShippingAddressId: (id: string) => void;
  addressOptions: Array<{ value: string; label: string }>;
  shippingMethods: Array<{ id: string; name: string; description: string; priceLabel: string }>;
  selectedShippingMethodId: string;
  setSelectedShippingMethodId: (id: string) => void;
  proofFiles: Array<{ id: string; name: string }>;
  fileQuantities: Record<string, number>;
  setFileQuantity: (fileId: string, qty: number) => void;
  confirmLabel?: string;
  onAddNewAddress: () => void;
  onClose: () => void;
  onConfirm: () => void;
  poNumber?: string;
  setPoNumber?: (value: string) => void;
}

type AddressOption = { value: string; label: string };
type WizardOrderType = "shipping" | "installation" | null;

const INSTALL_METHOD_ID = "Install";

const WIZARD_TITLES = ["Billing & Shipping Address", "Order Details", "Review & Approve"];
const WIZARD_SUBS = [
  "Step 1 of 3 · Required to approve this design",
  "Step 2 of 3 · Quantities and shipping",
  "Step 3 of 3 · Confirm your order",
];
const STEP_LABELS = ["Addresses", "Order Details", "Review"];

function CloseSvg() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
      <path
        fill="#6b7280"
        d="M12.96 1.04a1 1 0 0 0-1.41 0L7 5.59 2.45 1.04A1 1 0 1 0 1.04 2.45L5.59 7 1.04 11.55a1 1 0 1 0 1.41 1.41L7 8.41l4.55 4.55a1 1 0 0 0 1.41-1.41L8.41 7l4.55-4.55a1 1 0 0 0 0-1.41z"
      />
    </svg>
  );
}

function ChevronDownSvg() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6c757d" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

const DropdownIndicator = (props: any) => (
  <components.DropdownIndicator {...props}>
    <ChevronDownSvg />
  </components.DropdownIndicator>
);

const wizardSelectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    minHeight: 42,
    borderRadius: 8,
    borderColor: state.selectProps["aria-invalid"] ? "#ef4444" : "#e5e7eb",
    boxShadow: "none",
    fontSize: 14,
    fontFamily: "Roboto, sans-serif",
    "&:hover": {
      borderColor: state.selectProps["aria-invalid"] ? "#ef4444" : "#e5e7eb",
    },
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    padding: "2px 12px",
    fontFamily: "Roboto, sans-serif",
  }),
  placeholder: (provided: any) => ({
    ...provided,
    color: "#9ca3af",
    fontFamily: "Roboto, sans-serif",
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: "#111827",
    fontFamily: "Roboto, sans-serif",
  }),
  input: (provided: any) => ({
    ...provided,
    fontFamily: "Roboto, sans-serif",
  }),
  option: (provided: any) => ({
    ...provided,
    fontFamily: "Roboto, sans-serif",
    fontSize: 14,
  }),
  menu: (provided: any) => ({
    ...provided,
    zIndex: 10001,
    fontFamily: "Roboto, sans-serif",
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    display: "flex",
    opacity: 1,
    visibility: "visible",
  }),
  indicatorSeparator: (provided: any) => ({
    ...provided,
    display: "none",
  }),
};

function CheckSvg({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function TruckSvg() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="3" width="15" height="13" />
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  );
}

function WrenchSvg() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  );
}

function HelpPanel({ step }: { step: number }) {
  if (step === 1) {
    return (
      <>
        <div style={{ fontWeight: 700, marginBottom: 7, fontSize: 14 }}>Step 1 — Addresses</div>
        <ol style={{ margin: 0, paddingLeft: 18 }}>
          <li style={{ marginBottom: 5 }}>
            Click <strong style={{ color: "#16a34a" }}>+ Add New Address</strong> at the top to add any address — it saves to your account and becomes available for <em>both</em> billing and shipping dropdowns.
          </li>
          <li style={{ marginBottom: 5 }}>Use the <strong>Billing Address</strong> dropdown to pick your billing address.</li>
          <li style={{ marginBottom: 5 }}>Use the <strong>Shipping / Installation Address</strong> dropdown to pick a different delivery or site address.</li>
          <li>If both are the same location, check <strong>"Billing and Shipping address are the same"</strong> — you only need to fill one.</li>
        </ol>
      </>
    );
  }
  if (step === 2) {
    return (
      <>
        <div style={{ fontWeight: 700, marginBottom: 7, fontSize: 14 }}>Step 2 — Order Details</div>
        <ol style={{ margin: 0, paddingLeft: 18 }}>
          <li style={{ marginBottom: 5 }}><strong>PO Number</strong> is optional — enter it if your company requires a purchase order number for invoices.</li>
          <li style={{ marginBottom: 5 }}><strong>Quantity per File</strong> — enter how many prints you need for each design file. Minimum is 1.</li>
          <li><strong>Shipping Method</strong> — choose Pickup (Atlanta only, free), Farmer Delivery, or UPS (price quoted after order). For installation orders, shipping method is not needed.</li>
        </ol>
      </>
    );
  }
  return (
    <>
      <div style={{ fontWeight: 700, marginBottom: 7, fontSize: 14 }}>Step 3 — Review &amp; Approve</div>
      <ol style={{ margin: 0, paddingLeft: 18 }}>
        <li style={{ marginBottom: 5 }}>Review all your details before approving. Once you click <strong>Approve Design</strong>, your order is submitted and production begins.</li>
        <li style={{ marginBottom: 5 }}>If anything looks wrong, use the <strong>Back</strong> button to go back and edit.</li>
        <li>Your agent will receive the approval confirmation and follow up with next steps.</li>
      </ol>
    </>
  );
}

function ReviewRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "10px 0", borderBottom: "1px solid #f1f5f9" }}>
      <div className="bsa-review-label">{label}</div>
      <div className="bsa-review-value">{children}</div>
    </div>
  );
}

export function BillingShippingApprovalModal({
  open,
  loading = false,
  sameAsBilling,
  setSameAsBilling,
  selectedBillingAddressId,
  setSelectedBillingAddressId,
  selectedShippingAddressId,
  setSelectedShippingAddressId,
  addressOptions,
  shippingMethods,
  selectedShippingMethodId,
  setSelectedShippingMethodId,
  proofFiles,
  fileQuantities,
  setFileQuantity,
  confirmLabel = "Confirm & Approve Design",
  onAddNewAddress,
  onClose,
  onConfirm,
  poNumber = "",
  setPoNumber,
}: BillingShippingApprovalModalProps) {
  const [wizardStep, setWizardStep] = useState(1);
  const [orderType, setOrderType] = useState<WizardOrderType>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [hoveredShippingMethodId, setHoveredShippingMethodId] = useState("");
  const [orderTypePromptError, setOrderTypePromptError] = useState(false);
  const [billingError, setBillingError] = useState(false);
  const [shippingError, setShippingError] = useState(false);
  const [qtyError, setQtyError] = useState(false);
  const [shipMethodError, setShipMethodError] = useState(false);

  useEffect(() => {
    if (!open) return;
    setWizardStep(1);
    setHelpOpen(false);
    setOrderTypePromptError(false);
    setBillingError(false);
    setShippingError(false);
    setQtyError(false);
    setShipMethodError(false);
    if (/save/i.test(confirmLabel)) {
      setOrderType(selectedShippingMethodId === INSTALL_METHOD_ID ? "installation" : "shipping");
    } else {
      setOrderType(null);
    }
    // Reset only when the modal opens so picking Install does not wipe order type.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const normalizedAddressOptions = addressOptions.filter(
    (opt): opt is AddressOption => Boolean(opt?.value && opt?.label)
  );
  const visibleShippingMethods = shippingMethods.filter((method) => method.id !== INSTALL_METHOD_ID);
  const isInstall = orderType === "installation";
  const shippingAddressTitle = isInstall ? "Installation Address" : "Shipping Address";
  const sameAddrText = isInstall
    ? "Billing and Installation address are the same"
    : "Billing and Shipping address are the same";
  const approveButtonText = /save/i.test(confirmLabel) ? confirmLabel : "Approve Design";

  const hasInvalidQuantity =
    proofFiles.length > 0 &&
    proofFiles.some((file) => {
      const qty = Number(fileQuantities[file.id] ?? 0);
      return !Number.isFinite(qty) || Math.floor(qty) <= 0;
    });

  const handleSelectOrderType = (type: "shipping" | "installation") => {
    setOrderType(type);
    setOrderTypePromptError(false);
    if (type === "installation") {
      setSelectedShippingMethodId(INSTALL_METHOD_ID);
      return;
    }
    if (selectedShippingMethodId === INSTALL_METHOD_ID) {
      setSelectedShippingMethodId("");
    }
  };

  const validateStep1 = () => {
    if (!orderType) {
      setOrderTypePromptError(true);
      return false;
    }
    let valid = true;
    if (!selectedBillingAddressId) {
      setBillingError(true);
      valid = false;
    } else {
      setBillingError(false);
    }
    if (!sameAsBilling && !selectedShippingAddressId) {
      setShippingError(true);
      valid = false;
    } else {
      setShippingError(false);
    }
    return valid;
  };

  const validateStep2 = () => {
    let valid = true;
    if (hasInvalidQuantity) {
      setQtyError(true);
      showError("All files quantity must be more than zero.");
      valid = false;
    } else {
      setQtyError(false);
    }
    if (!isInstall && !selectedShippingMethodId) {
      setShipMethodError(true);
      valid = false;
    } else {
      setShipMethodError(false);
    }
    return valid;
  };

  const handleContinue = () => {
    if (wizardStep === 1 && !validateStep1()) return;
    if (wizardStep === 2 && !validateStep2()) return;
    if (wizardStep < 3) {
      setHelpOpen(false);
      setWizardStep((prev) => prev + 1);
    }
  };

  const handleConfirmClick = () => {
    if (!validateStep1() || !validateStep2()) return;
    onConfirm();
  };

  const selectedBilling = normalizedAddressOptions.find((opt) => opt.value === selectedBillingAddressId) || null;
  const selectedShipping = normalizedAddressOptions.find((opt) => opt.value === selectedShippingAddressId) || null;
  const selectedShipMethod =
    visibleShippingMethods.find((method) => method.id === selectedShippingMethodId) || null;

  const reviewShipMethodText = selectedShipMethod
    ? `${selectedShipMethod.name} — ${selectedShipMethod.priceLabel}`
    : "";
  const reviewShipMethodColor =
    selectedShipMethod?.priceLabel === "FREE"
      ? "#16a34a"
      : selectedShipMethod?.priceLabel === "TBD"
        ? "#9ca3af"
        : "#111827";

  return (
    <div
      onClick={onClose}
      className="billing-shipping-modal-overlay"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(17, 24, 39, 0.45)",
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(2px)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
        boxSizing: "border-box",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="billing-shipping-approval-modal-card"
        style={{
          width: "100%",
          maxWidth: 540,
          borderRadius: 14,
          overflow: "hidden",
          background: "#fff",
          border: "1px solid #e5e7eb",
          boxShadow: "0 24px 48px rgba(0,0,0,.18), 0 4px 12px rgba(0,0,0,.08)",
          fontFamily: "Roboto, sans-serif",
        }}
      >
        <div
          className="billing-shipping-approval-modal-header"
          style={{ padding: "18px 24px 0", borderBottom: "none", background: "#fff", flexShrink: 0 }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ width: 28, flexShrink: 0 }} />
            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 600, color: "#111827", marginBottom: 4 }}>
                {WIZARD_TITLES[wizardStep - 1]}
              </div>
              <div style={{ fontSize: 14, color: "#6b7280", marginBottom: 8 }}>{WIZARD_SUBS[wizardStep - 1]}</div>
              <button
                type="button"
                onClick={() => setHelpOpen((prev) => !prev)}
                style={{
                  background: "#f0f7ff",
                  border: "1.5px solid #bfdbfe",
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#2563EB",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "4px 10px",
                  borderRadius: 20,
                  fontFamily: "inherit",
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                Need help?
              </button>
            </div>
            <button
              type="button"
              className="bsa-close-btn"
              onClick={onClose}
              disabled={loading}
              aria-label="Close"
            >
              <CloseSvg />
            </button>
          </div>

          <div className="bsa-stepper-wrap" style={{ padding: "12px 0 40px" }}>
            <div style={{ display: "flex", alignItems: "flex-start" }}>
              {[1, 2, 3].map((step, index) => {
                const done = wizardStep > step;
                const active = wizardStep === step;
                const labelColor = done ? "#16a34a" : active ? "#2563EB" : "#9ca3af";
                const labelWeight = done || active ? 700 : 500;
                return (
                  <React.Fragment key={step}>
                    <div style={{ width: 28, flexShrink: 0, position: "relative" }}>
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 12,
                          fontWeight: 700,
                          background: done ? "#16a34a" : active ? "#2563EB" : "#e5e7eb",
                          color: done || active ? "#fff" : "#9ca3af",
                          boxShadow: active ? "0 0 0 3px rgba(37,99,235,.2)" : "none",
                        }}
                      >
                        {done ? <CheckSvg /> : step}
                      </div>
                      <div
                        style={{
                          position: "absolute",
                          top: 34,
                          left: "50%",
                          transform: "translateX(-50%)",
                          whiteSpace: "nowrap",
                          fontSize: 14,
                          fontWeight: labelWeight,
                          color: labelColor,
                          textAlign: "center",
                        }}
                      >
                        {STEP_LABELS[index]}
                      </div>
                    </div>
                    {index < 2 ? (
                      <div
                        style={{
                          flex: 1,
                          height: 2,
                          marginTop: 13,
                          background: wizardStep > step ? "#16a34a" : "#e5e7eb",
                        }}
                      />
                    ) : null}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>

        <div className="billing-shipping-approval-modal-scroll" style={{ padding: "4px 24px 20px" }}>
          {helpOpen ? (
            <div
              className="bsa-help-panel"
              style={{
                margin: "0 0 12px",
                padding: "13px 15px",
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: 10,
                fontSize: 14,
                color: "#1e40af",
                lineHeight: 1.7,
                textAlign: "left",
              }}
            >
              <HelpPanel step={wizardStep} />
            </div>
          ) : null}
          {wizardStep === 1 ? (
            <div>
              <div style={{ fontSize: 18, fontWeight: 600, color: "#374151", marginBottom: 10, textAlign: "center", letterSpacing: "0.01em" }}>
                What is this order for? <span style={{ color: "#ef4444" }}>*</span>
              </div>
              <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
                {(
                  [
                    { id: "shipping" as const, title: "Shipping", icon: TruckSvg },
                    { id: "installation" as const, title: "Installation", icon: WrenchSvg },
                  ]
                ).map((option) => {
                  const selected = orderType === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleSelectOrderType(option.id)}
                      className="order-type-btn"
                      data-selected={selected ? "true" : "false"}
                      style={{
                        flex: 1,
                        padding: "14px 12px",
                        borderRadius: 12,
                        border: selected ? "2px solid #2563EB" : "2px solid #d1d5db",
                        background: selected ? "#1d4ed8" : "#f9fafb",
                        cursor: "pointer",
                        fontFamily: "Roboto, sans-serif",
                        fontSize: 16,
                        fontWeight: selected ? 700 : 600,
                        color: selected ? "#fff" : "#6b7280",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 7,
                        boxShadow: selected ? "0 2px 8px rgba(37,99,235,.35)" : "0 1px 3px rgba(0,0,0,.06)",
                      }}
                    >
                      <option.icon />
                      {option.title}
                    </button>
                  );
                })}
              </div>

              {!orderType ? (
                <div
                  style={{
                    marginTop: 10,
                    textAlign: "center",
                    fontSize: 14,
                    color: orderTypePromptError ? "#ef4444" : "#6b7280",
                    fontWeight: orderTypePromptError ? 600 : 400,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 5,
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={orderTypePromptError ? "#ef4444" : "#9ca3af"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 8 12 12 14 14" />
                  </svg>
                  Select an order type above to continue
                </div>
              ) : (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 16,
                      gap: 12,
                      padding: "12px 14px",
                      background: "#fff8f8",
                      border: "1px solid #fecaca",
                      borderRadius: 10,
                    }}
                  >
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: "#dc2626", flex: 1, lineHeight: 1.5 }}>
                      Select a saved address below, or add a new one — it becomes available for both billing & shipping.
                    </p>
                    <button
                      type="button"
                      className="billing-add-new-address-btn"
                      onClick={onAddNewAddress}
                      style={{
                        flexShrink: 0,
                        whiteSpace: "nowrap",
                        padding: "8px 16px",
                        borderRadius: 8,
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#fff",
                        background: "#16a34a",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      + Add New Address
                    </button>
                  </div>

                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      cursor: "pointer",
                      padding: "12px 14px",
                      background: "#f8fafc",
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                      marginBottom: 16,
                      userSelect: "none",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={sameAsBilling}
                      onChange={(e) => {
                        setSameAsBilling(e.target.checked);
                        if (e.target.checked) setShippingError(false);
                      }}
                      style={{ width: 18, height: 18, flexShrink: 0 }}
                    />
                    <span style={{ fontSize: 14, color: "#374151", fontWeight: 500 }}>{sameAddrText}</span>
                  </label>

                  <div style={{ marginBottom: 18 }}>
                    <label className="bsa-field-label">
                      Billing Address <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <Select
                      inputId="billingAddressId"
                      name="billingAddressId"
                      options={normalizedAddressOptions}
                      isClearable
                      placeholder="Select existing address…"
                      value={selectedBilling}
                      onChange={(selected) => {
                        setSelectedBillingAddressId(selected?.value || "");
                        setBillingError(false);
                      }}
                      components={{ DropdownIndicator }}
                      styles={wizardSelectStyles}
                      classNamePrefix="rs"
                      aria-invalid={billingError}
                    />
                    {billingError ? (
                      <div style={{ fontSize: 14, color: "#ef4444", marginTop: 3 }}>Please select a billing address.</div>
                    ) : (
                      <div style={{ fontSize: 14, color: "#6b7280", marginTop: 4 }}>
                        Select a saved address or click <strong>+ Add New Address</strong> to add another.
                      </div>
                    )}
                  </div>

                  {!sameAsBilling ? (
                    <div style={{ marginBottom: 8 }}>
                      <label className="bsa-field-label">
                        {shippingAddressTitle} <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <Select
                        inputId="shippingAddressId"
                        name="shippingAddressId"
                        options={normalizedAddressOptions}
                        isClearable
                        placeholder="Select existing address…"
                        value={selectedShipping}
                        onChange={(selected) => {
                          setSelectedShippingAddressId(selected?.value || "");
                          setShippingError(false);
                        }}
                        components={{ DropdownIndicator }}
                        styles={wizardSelectStyles}
                        classNamePrefix="rs"
                        aria-invalid={shippingError}
                      />
                      {shippingError ? (
                        <div style={{ fontSize: 14, color: "#ef4444", marginTop: 3 }}>Please select a shipping address.</div>
                      ) : (
                        <div style={{ fontSize: 14, color: "#6b7280", marginTop: 4 }}>Select a saved address from the list.</div>
                      )}
                    </div>
                  ) : null}
                </>
              )}
            </div>
          ) : null}

          {wizardStep === 2 ? (
            <div>
              <div style={{ marginBottom: 20 }}>
                <label className="bsa-po-label">
                  PO # <span style={{ fontWeight: 400, color: "#9ca3af" }}>(optional)</span>
                </label>
                <input
                  type="text"
                  value={poNumber}
                  onChange={(e) => setPoNumber?.(e.target.value)}
                  placeholder="Enter purchase order number…"
                  style={{
                    width: "100%",
                    maxWidth: 280,
                    padding: "9px 12px",
                    fontSize: 14,
                    fontFamily: "Roboto, sans-serif",
                    border: "1px solid #d1d5db",
                    borderRadius: 7,
                    outline: "none",
                    color: "#111827",
                    background: "#f3f4f6",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label className="bsa-field-label">
                  Quantity per File <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {proofFiles.length === 0 ? (
                    <div style={{ fontSize: 14, color: "#6b7280" }}>No proof files available.</div>
                  ) : (
                    proofFiles.map((file) => {
                      const currentQty = Number(fileQuantities[file.id] ?? 0);
                      const normalizedQty =
                        Number.isFinite(currentQty) && currentQty >= 0 ? Math.floor(currentQty) : 0;
                      return (
                        <div
                          key={file.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "9px 12px",
                            border: "1px solid #e5e7eb",
                            borderRadius: 8,
                            background: "#fafafa",
                          }}
                        >
                          <div
                            title={file.name}
                            style={{
                              flex: 1,
                              minWidth: 0,
                              fontSize: 14,
                              fontWeight: 500,
                              color: "#1f2937",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {file.name}
                          </div>
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              padding: 4,
                              border: "1px solid #e5e7eb",
                              borderRadius: 14,
                              background: "#fff",
                              flexShrink: 0,
                            }}
                          >
                            <button
                              type="button"
                              aria-label="Decrease quantity"
                              onClick={() => setFileQuantity(file.id, Math.max(0, normalizedQty - 1))}
                              style={{
                                width: 36,
                                height: 36,
                                border: 0,
                                borderRadius: 10,
                                background: "#f8fafc",
                                color: "#4b5563",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: 0,
                                lineHeight: 0,
                              }}
                            >
                              <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                                <rect x="1.5" y="5.25" width="9" height="1.5" rx="0.75" fill="currentColor" />
                              </svg>
                            </button>
                            <input
                              type="text"
                              inputMode="numeric"
                              placeholder="1"
                              value={normalizedQty === 0 ? "" : String(normalizedQty)}
                              onChange={(e) => {
                                const raw = e.target.value.replace(/\D/g, "");
                                setFileQuantity(file.id, raw === "" ? 0 : Math.floor(Number(raw)));
                                setQtyError(false);
                              }}
                              style={{
                                width: 36,
                                height: 36,
                                border: 0,
                                outline: "none",
                                textAlign: "center",
                                fontSize: 16,
                                fontWeight: 600,
                                color: "#4b5563",
                                background: "transparent",
                                lineHeight: "36px",
                                padding: 0,
                              }}
                            />
                            <button
                              type="button"
                              aria-label="Increase quantity"
                              onClick={() => setFileQuantity(file.id, normalizedQty + 1)}
                              style={{
                                width: 36,
                                height: 36,
                                border: 0,
                                borderRadius: 10,
                                background: "#f8fafc",
                                color: "#4b5563",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: 0,
                                lineHeight: 0,
                              }}
                            >
                              <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                                <rect x="1.5" y="5.25" width="9" height="1.5" rx="0.75" fill="currentColor" />
                                <rect x="5.25" y="1.5" width="1.5" height="9" rx="0.75" fill="currentColor" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                {qtyError ? (
                  <div style={{ fontSize: 14, color: "#ef4444", marginTop: 3 }}>Please enter a valid quantity for all files.</div>
                ) : null}
              </div>

              {!isInstall ? (
                <div>
                  <label className="bsa-field-label">
                    Shipping Method <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {visibleShippingMethods.map((method) => {
                      const isSelected = selectedShippingMethodId === method.id;
                      const isHovered = hoveredShippingMethodId === method.id;
                      return (
                        <label
                          key={method.id}
                          className="bsa-method-option"
                          onMouseEnter={() => setHoveredShippingMethodId(method.id)}
                          onMouseLeave={() => setHoveredShippingMethodId("")}
                          style={{ cursor: "pointer" }}
                        >
                          <input
                            type="radio"
                            name="shippingMethod"
                            checked={isSelected}
                            onChange={() => {
                              setSelectedShippingMethodId(method.id);
                              setShipMethodError(false);
                            }}
                            style={{ display: "none" }}
                          />
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                              padding: "12px 14px",
                              border: isSelected ? "1.5px solid #2563EB" : isHovered ? "1.5px solid #bfdbfe" : "1.5px solid #e5e7eb",
                              borderRadius: 9,
                              background: isSelected ? "#eff6ff" : isHovered ? "#f8faff" : "#fff",
                              boxShadow: isSelected ? "0 0 0 3px rgba(37,99,235,.08)" : "none",
                            }}
                          >
                            <span
                              style={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                border: isSelected ? "2px solid #2563EB" : "2px solid #d1d5db",
                                background: isSelected ? "#2563EB" : "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              {isSelected ? (
                                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff", display: "block" }} />
                              ) : null}
                            </span>
                            <span style={{ flex: 1 }}>
                              <div className="bsa-method-name">{method.name}</div>
                              <div className="bsa-method-sub">{method.description}</div>
                            </span>
                            <span
                              className="bsa-method-name"
                              style={{
                                fontWeight: method.priceLabel === "TBD" ? 500 : 700,
                                fontStyle: method.priceLabel === "TBD" ? "italic" : "normal",
                                color:
                                  method.priceLabel === "FREE"
                                    ? "#16a34a"
                                    : method.priceLabel === "TBD"
                                      ? "#9ca3af"
                                      : "#1f2937",
                                flexShrink: 0,
                              }}
                            >
                              {method.priceLabel}
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  {shipMethodError ? (
                    <div style={{ fontSize: 14, color: "#ef4444", marginTop: 3 }}>Please select a shipping method.</div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          {wizardStep === 3 ? (
            <div className="billing-review-list" style={{ padding: "4px 0 8px" }}>
              <div style={{ fontSize: 14, color: "#6b7280", marginBottom: 12 }}>Review your order details before approving.</div>
              <ReviewRow label="Order Type">
                <span style={{ color: isInstall ? "#7c3aed" : "#2563EB", fontWeight: 600, textTransform: "capitalize" }}>
                  {isInstall ? "Installation" : "Shipping"}
                </span>
              </ReviewRow>
              <ReviewRow label="Billing Address">{selectedBilling?.label || "—"}</ReviewRow>
              <ReviewRow label={shippingAddressTitle}>
                {sameAsBilling ? (
                  <span style={{ color: "#6b7280", fontWeight: 400, fontStyle: "normal" }}>Same as billing</span>
                ) : (
                  selectedShipping?.label || "—"
                )}
              </ReviewRow>
              {poNumber.trim() ? <ReviewRow label="PO #">{poNumber.trim()}</ReviewRow> : null}
              <ReviewRow label="Quantities">
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {proofFiles.map((file) => (
                    <div key={file.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 16, color: "#374151" }}>
                      <span style={{ color: "#6b7280" }}>{file.name}</span>
                      <span style={{ fontWeight: 700, color: "#111827" }}>× {Number(fileQuantities[file.id] ?? 0)}</span>
                    </div>
                  ))}
                </div>
              </ReviewRow>
              {!isInstall && selectedShipMethod ? (
                <ReviewRow label="Shipping Method">
                  <span style={{ color: reviewShipMethodColor }}>{reviewShipMethodText}</span>
                </ReviewRow>
              ) : null}
            </div>
          ) : null}
        </div>

        <div
          className="billing-shipping-approval-modal-footer"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 24px",
            borderTop: "1px solid #f1f5f9",
            background: "#fafafa",
            gap: 10,
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={() => {
              if (wizardStep > 1) {
                setHelpOpen(false);
                setWizardStep((prev) => prev - 1);
              }
            }}
            disabled={loading}
            className="billing-shipping-footer-btn billing-shipping-footer-btn-secondary billing-shipping-footer-back"
            style={{ visibility: wizardStep > 1 ? "visible" : "hidden" }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </button>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="billing-shipping-footer-btn billing-shipping-footer-btn-secondary billing-shipping-footer-cancel"
            >
              Cancel
            </button>
            {wizardStep < 3 ? (
              <button
                type="button"
                onClick={handleContinue}
                disabled={loading}
                className="billing-shipping-footer-btn billing-shipping-footer-btn-primary billing-shipping-footer-confirm"
              >
                Continue
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            ) : (
              <button
                type="button"
                onClick={handleConfirmClick}
                disabled={loading}
                className="billing-shipping-footer-btn billing-shipping-footer-btn-approve billing-shipping-footer-confirm"
              >
                <CheckSvg size={14} />
                {approveButtonText}
              </button>
            )}
          </div>
        </div>

        <style>{`
          .billing-shipping-modal-overlay,
          .billing-shipping-approval-modal-card,
          .billing-shipping-approval-modal-card *,
          .billing-shipping-approval-modal-card button,
          .billing-shipping-approval-modal-card input,
          .billing-shipping-approval-modal-card label,
          .billing-shipping-approval-modal-card p,
          .billing-shipping-approval-modal-card span,
          .billing-shipping-approval-modal-card div,
          .billing-shipping-approval-modal-card .rs__control,
          .billing-shipping-approval-modal-card .rs__single-value,
          .billing-shipping-approval-modal-card .rs__placeholder,
          .billing-shipping-approval-modal-card .rs__input,
          .billing-shipping-approval-modal-card .rs__option {
            font-family: Roboto, sans-serif !important;
          }
          .billing-shipping-approval-modal-card label.bsa-field-label {
            font-size: 16px !important;
            font-weight: 600 !important;
            color: #1f2937 !important;
            display: block;
            margin-bottom: 7px;
            line-height: 1.3;
          }
          .billing-shipping-approval-modal-card label.bsa-po-label {
            font-size: 14px !important;
            font-weight: 600 !important;
            color: #1f2937 !important;
            display: block;
            margin-bottom: 7px;
          }
          .billing-shipping-approval-modal-card label.bsa-method-option {
            font-size: 14px !important;
          }
          .billing-shipping-approval-modal-card .bsa-method-name {
            font-size: 14px !important;
            font-weight: 600;
            color: #111827;
          }
          .billing-shipping-approval-modal-card .bsa-method-sub {
            font-size: 12px !important;
            color: #6b7280;
            margin-top: 2px;
          }
          .billing-shipping-approval-modal-card .bsa-review-label {
            font-size: 14px !important;
            font-weight: 600;
            color: #6b7280;
            text-transform: uppercase;
            letter-spacing: 0.04em;
          }
          .billing-shipping-approval-modal-card .bsa-review-value {
            font-size: 16px !important;
            font-weight: 500;
            color: #111827;
          }
          .billing-shipping-approval-modal-card .billing-shipping-footer-btn {
            appearance: none !important;
            -webkit-appearance: none !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 8px !important;
            flex-shrink: 0 !important;
            min-width: 104px !important;
            height: 44px !important;
            padding: 0 18px !important;
            margin: 0 !important;
            border-radius: 12px !important;
            font-size: 14px !important;
            font-weight: 700 !important;
            font-family: Roboto, sans-serif !important;
            line-height: 1 !important;
            cursor: pointer !important;
            box-shadow: 0 1px 2px rgba(37, 99, 235, 0.2) !important;
          }
          .billing-shipping-approval-modal-card .billing-shipping-footer-btn svg {
            display: block !important;
            width: 14px !important;
            height: 14px !important;
            flex-shrink: 0;
          }
          .billing-shipping-approval-modal-card .billing-shipping-footer-btn-primary {
            background: #1d4ed8 !important;
            border: 1px solid #7c9fe8 !important;
            color: #fff !important;
          }
          .billing-shipping-approval-modal-card .billing-shipping-footer-btn-primary:hover:not(:disabled) {
            background: #1d4ed8 !important;
            border-color: #7c9fe8 !important;
          }
          .billing-shipping-approval-modal-card .billing-shipping-footer-btn-approve {
            background: linear-gradient(135deg, #16a34a, #15803d) !important;
            border: none !important;
            color: #fff !important;
            box-shadow: 0 2px 8px rgba(22, 163, 74, 0.3) !important;
          }
          .billing-shipping-approval-modal-card .billing-shipping-footer-btn-approve:hover:not(:disabled) {
            background: linear-gradient(135deg, #16a34a, #15803d) !important;
            border: none !important;
          }
          .billing-shipping-approval-modal-card .billing-shipping-footer-btn-secondary {
            background: #fff !important;
            border: 1px solid #7c9fe8 !important;
            color: #1d4ed8 !important;
            box-shadow: none !important;
          }
          .billing-shipping-approval-modal-card .billing-shipping-footer-btn-secondary:hover:not(:disabled) {
            background: #eff6ff !important;
            border-color: #7c9fe8 !important;
          }
          .billing-shipping-approval-modal-card .billing-shipping-footer-btn:disabled {
            opacity: 0.45 !important;
            cursor: not-allowed !important;
          }
          .billing-shipping-approval-modal-card .bsa-close-btn {
            appearance: none !important;
            -webkit-appearance: none !important;
            width: 32px !important;
            height: 32px !important;
            min-width: 32px !important;
            min-height: 32px !important;
            padding: 0 !important;
            margin: 2px 0 0 0 !important;
            border: 0 !important;
            border-radius: 8px !important;
            background: transparent !important;
            box-shadow: none !important;
            color: #6b7280 !important;
            cursor: pointer !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            line-height: 0 !important;
            flex-shrink: 0 !important;
            overflow: visible !important;
          }
          .billing-shipping-approval-modal-card .bsa-close-btn:hover {
            background: #f3f4f6 !important;
            box-shadow: none !important;
          }
          .billing-shipping-approval-modal-card .bsa-close-btn svg {
            display: block !important;
            width: 14px !important;
            height: 14px !important;
            flex-shrink: 0;
          }
          .billing-shipping-approval-modal-card .rs__dropdown-indicator svg {
            display: block !important;
            width: 18px !important;
            height: 18px !important;
          }
          .billing-add-new-address-btn:hover {
            background: #15803d !important;
          }
          .order-type-btn {
            transition: all .18s;
          }
          .order-type-btn[data-selected="false"]:hover {
            border-color: #2563EB !important;
            background: #eff6ff !important;
            color: #1e40af !important;
            box-shadow: 0 2px 6px rgba(37,99,235,.15) !important;
          }
          .billing-review-list > div:last-child {
            border-bottom: none;
          }
          .billing-shipping-approval-modal-card {
            display: flex;
            flex-direction: column;
            min-height: 0;
            max-height: min(92vh, calc(100dvh - 48px), calc(100vh - 48px));
          }
          .billing-shipping-approval-modal-header {
            flex-shrink: 0;
          }
          .billing-shipping-approval-modal-scroll {
            flex: 1;
            min-height: 0;
            overflow-y: auto;
            overflow-x: hidden;
            -webkit-overflow-scrolling: touch;
            scrollbar-gutter: stable;
            scrollbar-width: auto;
          }
          .billing-shipping-approval-modal-scroll::-webkit-scrollbar {
            width: 10px;
          }
          .billing-shipping-approval-modal-scroll::-webkit-scrollbar-track {
            background: #e8ecf1;
            border-radius: 8px;
          }
          .billing-shipping-approval-modal-scroll::-webkit-scrollbar-thumb {
            background: #94a3b8;
            border-radius: 8px;
          }
          .billing-shipping-approval-modal-scroll::-webkit-scrollbar-thumb:hover {
            background: #64748b;
          }
          .billing-shipping-approval-modal-footer {
            flex-shrink: 0;
          }
          @media (max-width: 575.98px) {
            .billing-shipping-modal-overlay {
              align-items: stretch;
              justify-content: center;
              overflow: hidden;
              padding: max(8px, env(safe-area-inset-top, 0px)) 8px max(8px, env(safe-area-inset-bottom, 0px));
            }
            .billing-shipping-approval-modal-card {
              height: min(
                calc(100svh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 16px),
                calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 16px),
                calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 16px)
              ) !important;
              max-height: min(
                calc(100svh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 16px),
                calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 16px),
                calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 16px)
              ) !important;
              margin: auto 0;
            }
            .billing-shipping-approval-modal-header {
              padding-left: 16px !important;
              padding-right: 16px !important;
            }
            .bsa-stepper-wrap {
              padding: 8px 0 32px !important;
            }
            .billing-shipping-approval-modal-card .bsa-help-panel {
              max-height: min(200px, 32svh);
              overflow-y: scroll !important;
              -webkit-overflow-scrolling: touch;
              scrollbar-gutter: stable;
              scrollbar-width: auto;
              scrollbar-color: #94a3b8 #dbe7f3;
            }
            .billing-shipping-approval-modal-card .bsa-help-panel::-webkit-scrollbar {
              width: 10px;
            }
            .billing-shipping-approval-modal-card .bsa-help-panel::-webkit-scrollbar-track {
              background: #dbe7f3;
              border-radius: 8px;
            }
            .billing-shipping-approval-modal-card .bsa-help-panel::-webkit-scrollbar-thumb {
              background: #94a3b8;
              border-radius: 8px;
            }
            .billing-shipping-approval-modal-scroll {
              overflow-y: scroll !important;
              scrollbar-width: auto;
              scrollbar-color: #94a3b8 #e8ecf1;
            }
            .billing-shipping-approval-modal-scroll::-webkit-scrollbar {
              width: 10px;
            }
            .billing-shipping-approval-modal-footer {
              flex-wrap: wrap;
            }
          }
        `}</style>
      </div>
    </div>
  );
}
